<script lang="ts">
import { ref } from 'vue'
import 'vue-pdf-embed/dist/styles/annotationLayer.css'
import 'vue-pdf-embed/dist/styles/textLayer.css'

import { useDesignStore } from '@/stores/designStore.ts'
import AbilityDisplayMedallion from '@/components/AbilityDisplayMedallion.vue'
import TextToFormattedComponentStructure from '@/components/TextToFormattedComponentStructure.vue'
import { BPopover } from 'bootstrap-vue-next'
import { useManualStore } from '@/stores/manualStore.ts'
import { useSpellStore } from '@/stores/spellsStore.ts'
import TitleMedallion from '@/components/TitleMedallion.vue'

export default {
  props: ['title'],
  setup(props, context) {
    const modal = ref(false)

    const designStore = useDesignStore()
    const spellStore = useSpellStore

    return {
      designStore,
      props,
      spellStore
    }
  },
  components: {
    AbilityDisplayMedallion,
    TextToFormattedComponentStructure,
    BPopover,
    TitleMedallion
  }
}
</script>
<template>
  <div
    style="
      display: flex;
      flex-direction: column;
      width: 100%;
      margin-left: 1rem;
      padding-right: 2rem;
    "
    :style="{ fontFamily: designStore.font }"
  >
    <div>
      <TextToFormattedComponentStructure
        title="main"
        text="Spells are known manifestations of magic that can be reliably reproduced once an individual has learned them. The act of performing a spell is spellcasting, or casting for short. An individual may learn them through intellectual study, meditation, worship, shady deals, inherent magical potential, etc. Spells can be cast how they are, finnessed to increase the power of an aspect of a spell, or modified through Metamagic."
        :definition-help="true"
      >
      </TextToFormattedComponentStructure>
      <div style="text-align: end; font-style: italic">
        Try hovering the parts of the spell header to read more...
      </div>
    </div>
    <TitleMedallion style="margin-bottom: 1rem" title="Example Spell"></TitleMedallion>
    <div style="margin: 1rem">
      <div
        class="abilityGrid"
        :style="{
          background: designStore.secondaryTheme,
          color: designStore.sidebarText,
          borderColor: designStore.secondaryTheme
        }"
      >
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div
            style="
              display: flex;
              flex-direction: column;
              justify-content: center;
              min-height: fit-content;
            "
          >
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; text-wrap: nowrap; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="action cost"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="action cost"
                      >Action Cost: Core Action
                    </span>
                  </template>
                  <div>
                    Action Cost is the action type required to perform the action. Once the action
                    is completed, whether succesful or not, the action cost is spent for that round
                    of combat.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div style="display: flex; flex-direction: column; justify-content: center; height: 100%">
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="Target"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="Target"
                      >Target: LOS 80' + 20'/F
                    </span>
                  </template>
                  <div>
                    Target refers to any restriction on who may be targeted, typically having to do
                    with the range of the ability. Here LOS 80' means anyone within 80' that the
                    caster can see is a potential target. The + 20'/F is called finnessing. If this
                    spell is finnessed for target, the LOS will increase by 20' for each finnesse
                    spent. This is not mandatory. See Finesse for the rules of finnessing.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div style="display: flex; flex-direction: column; justify-content: center; height: 100%">
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="area"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="area"
                      >Area: 5' Wide 80' + 5' Wide/2F Line
                    </span>
                  </template>
                  <div>
                    The area of the spell relates to the size of the effects. Sometimes effects will
                    only be able to effect one person, sometimes, a caster can pick multiple people,
                    or sometimes a spell has an AOE (area of effect). AOE means anyone within a
                    specific shape's area will be effected. In this example, everyone in a 5' Wide
                    line will be effected (enemies and allies alike) up to the targeted location. In
                    this example, the ( + 5' Wide/2F) is called finnessing and means that for every
                    two additional mana spent, the spell can be 5' wider.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div style="display: flex; flex-direction: column; justify-content: center; height: 100%">
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="evocation"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="evocation"
                      >Type: Evocation
                    </span>
                  </template>
                  <div>
                    The type of a spell can be Continuous, Focus, or Evocation. Evocation spells are
                    evoked and finished in an instant. Countinous means the spell will continue
                    until its duration ends or it is otherwise manually ended. Focus means that for
                    the duration of the spell the character must use their focus to keep the spell
                    going. If their boken shift or is broken, the spell immediately ends.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div style="display: flex; flex-direction: column; justify-content: center; height: 100%">
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="Duration"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="Duration"
                      >Duration: Instantaneous</span
                    >
                  </template>
                  <div>
                    The Duration conveys the length of time the spell will take effect. instantanous
                    spells happen in an instant and are done, if spells last longer they might be
                    labeled as lasting a number of rounds or a length of time. It also might specify
                    a spell as permnanent.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
        <div
          class="grid__item"
          :style="{
            background: designStore.sidebarBacking,
            borderColor: designStore.secondaryTheme
          }"
        >
          <div style="display: flex; flex-direction: column; justify-content: center; height: 100%">
            <div
              style="
                display: flex;
                justify-content: flex-start;
                height: 100%;
                padding-right: 0.5rem;
                flex-wrap: wrap;
              "
            >
              <div style="align-self: center; padding-right: 0.5rem">
                <BPopover
                  :style="{
                    background: designStore.inputBacking,
                    borderColor: designStore.secondaryTheme
                  }"
                  class="popover"
                  :close-on-hide="true"
                  :delay="{ show: 0, hide: 0 }"
                  style="width: fit-content !important; text-indent: 0rem"
                  :placement="'top'"
                  target="resistance"
                >
                  <template #target>
                    <span
                      class="hoverableText"
                      style="font-style: italic; cursor: pointer"
                      id="resistance"
                      >Resistance: Lightning,HEA
                    </span>
                  </template>
                  <div>
                    If a spell has a resistance check, it will be emphasized here. It will list the
                    attribute that targets must roll to attempt to save from the effects. Whats
                    happens if they save will be listed in the spell description itself. A damage
                    type might also be listed here, this is a reminder since some targets might have
                    resistance or be immune to spell depending on their resistances and immunities.
                  </div>
                </BPopover>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style="
          border-left: 2px solid;
          border-right: 2px solid;
          border-bottom: 4px solid;
          border-bottom-left-radius: 1rem;
          border-bottom-right-radius: 1rem;
          padding: 0.5rem;
          text-align: left;
        "
        :style="{
          background: designStore.inputBacking,
          color: designStore.inputText,
          borderColor: designStore.secondaryTheme,
          '--sidebar': designStore.sidebarBacking
        }"
      >
        <div
          style="z-index: 5; margin-left: 0.75rem; margin-right: 0.75rem; text-align: left"
          :style="{
            borderColor: designStore.secondaryTheme,
            '--secondary': designStore.secondaryTheme,
            '--primary': designStore.primaryTheme,
            '--primary-text': designStore.primaryText
          }"
        ></div>
        <TextToFormattedComponentStructure
          style="padding-left: 0.5rem; padding-right: 0.5rem"
          :calc-on="false"
          text="A massive bolt of lightning flies from the caster's fingertips in a line, inflicting 4 + 1/F d12 lightning damage, HEA save for half damage. "
          :title="title"
          :definition-help="true"
        ></TextToFormattedComponentStructure>
        <AbilityDisplayMedallion :medallion="'gi-entangled-typhoon'"></AbilityDisplayMedallion>
      </div>
      <div
        style="display: flex; justify-content: center; z-index: 1; width: 100%"
        :style="{
          color: designStore.secondaryTheme,
          borderColor: designStore.secondaryTheme
        }"
      >
        <v-icon
          name="gi-door-ring-handle"
          scale="2"
          style="
            position: relative;
            text-align: right;
            bottom: 0.25rem;
            transform: scale(1.25, 1);
            rotate: 180deg;
          "
        ></v-icon>
      </div>
      <div style="margin-top: 1rem; padding-left: 20%; text-align: end">
        <i>
          Note: If you are confused by something that looks like this + 1/F, that is Finesse. These
          are optional, but may be used to make spells more powerful in exchange for more mana.</i
        >
      </div>
    </div>
  </div>
</template>

<style>
.abilityGrid {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  z-index: 4;
  border: 2px solid;
  border-bottom: 2px solid;
  margin-top: -1.5rem;
  grid-gap: 0.05rem;
  justify-content: space-around;
}
.grid__item {
  padding: 0.25rem;
  position: relative;
  padding-left: 0.5rem;
  padding-bottom: 0.5rem;
  padding-top: 0.25rem;
  text-wrap: wrap;
}

.buttonStyle {
  border: 2px solid;
  padding: 0.35rem;
  border-radius: 1rem;
  background: var(--primary);
  border-color: inherit;
  color: var(--primary-text);
  cursor: pointer;
  white-space: nowrap;
  display: inline-block;
}
@media (max-width: 1000px) {
  .abilityGrid {
    grid-template-rows: auto auto auto;
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<!-- <template>
  <div>
    Spells are known manifestations of magic that can be reliably reproduced once an individual has
    learned them. The act of performing a spell is spellcasting, or casting for short. An individual
    may learn them through intellectual study, meditation, worship, shady deals, inherent magical
    potential, etc. Spells can be cast how they are, <i>finnessed</i> to increase the power of an
    aspect of a spell, or modified through <i>Metamagic</i>. Spells are written In a standardized
    format with the following properties: \n*Name: Identifier of the spell\n*Rank: Representation of
    the power of the spell, also, the amount of mana it costs to cast\n*Action Cost: the Action
    required to perform the spell\n*Target: A Restriction on who the spell may be used on whether
    that be based on an effective range, the ability is only restricted to work on the Caster, etc.
    \n*Area: Related to the amount of targets efected, whether this be an actual number of target
    that may be selected, an <i>Area of Effect</i> etc.\n*Type: Defines if and how the ability
    persists over time\n*Duration: Specifies how long the spell's effects last\n*Resistance:
    Reference for what kind of <i>resistance checks</i> or <i>saves</i> there might be
    <br /><br />If you are confused by something that looks like this + 1/F, that is <i>Finesse</i>.
    These are optional, but may be used to make spells more powerful in exchange for more mana.
  </div>
</template>
*/ -->
